import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import LanguageSwitcher from "../language-switcher/LanguageSwitcher";
import SwapText from "../swap-text/SwapText";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import "./Header.css";

const NAV_LINKS = [
  { href: "#experience", key: "navExperience" },
  { href: "#skills", key: "navSkills" },
  { href: "#contact", key: "navContact" },
] as const;

function Header() {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Collapse the mobile menu if the viewport grows past the breakpoint,
  // so it can't stay "open" behind the desktop layout.
  useEffect(() => {
    if (!isMobile) setMenuOpen(false);
  }, [isMobile]);

  return (
    <motion.header
      className="header"
      data-scrolled={scrolled}
      initial={prefersReducedMotion ? false : { y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <div className="header-bar">
        <a href="#hero" className="header-mark" onClick={() => setMenuOpen(false)}>
          YB
        </a>

        <nav className="header-nav" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <a key={link.href} href={link.href} className="header-nav-link">
              <SwapText tKey={`header.${link.key}`} />
            </a>
          ))}
        </nav>

        <div className="header-actions">
          <LanguageSwitcher />
          <a
            href="/Youssef_Bachaoui_CV_English.pdf"
            target="_blank"
            rel="noreferrer"
            className="header-resume"
          >
            <SwapText tKey="header.resume" />
          </a>
          <button
            type="button"
            className="header-menu-btn"
            aria-label={menuOpen ? t("header.menuClose") : t("header.menuOpen")}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            className="header-mobile-nav"
            aria-label="Mobile"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="header-mobile-link"
                onClick={() => setMenuOpen(false)}
              >
                <SwapText tKey={`header.${link.key}`} />
              </a>
            ))}
            <a
              href="/Youssef_Bachaoui_CV_English.pdf"
              target="_blank"
              rel="noreferrer"
              className="header-mobile-link"
              onClick={() => setMenuOpen(false)}
            >
              <SwapText tKey="header.resume" />
            </a>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}

export default Header;
